import { DebouncedFunc, isEqual } from 'lodash';
import {
    CadSearchResource,
    EventSearchForm,
    EventSearchPdfExportRequest,
    SearchVehicleFormType,
} from '@mark43/cad-api';
import { DeepPartialSkipArrayKey } from 'react-hook-form';
import { DUMMY_VALUE_TO_CLEAR_RADIO_GROUP } from '../search-windows/constants';
import { EventSearchFormType } from './types';
import {
    defaultSubPremiseDto,
    personFormDefaultValues,
    vehicleFormDefaultValues,
} from '../search-windows/configuration';
import { ModalName, ModalType } from '../modals/types';
import { UseMutationResult } from '@tanstack/react-query';

function prepareVehicleForSubmit(vehicle: DeepPartialSkipArrayKey<SearchVehicleFormType>) {
    const towType = vehicle.towType === DUMMY_VALUE_TO_CLEAR_RADIO_GROUP ? null : vehicle.towType;
    return { ...vehicle, towType };
}

export function prepareForSubmit(
    eventSearchForm: DeepPartialSkipArrayKey<EventSearchFormType>
): EventSearchForm {
    const eventNumbers =
        eventSearchForm.eventNumbers?.filter((eventNumber) => eventNumber.eventNumber?.length) ??
        [];
    const reportingEventNumbers =
        eventSearchForm.reportingEventNumbers?.filter(
            (reportingEventNumber) => reportingEventNumber?.reportingEventNumber?.length
        ) ?? [];
    const locationVerification =
        eventSearchForm.locationVerification === DUMMY_VALUE_TO_CLEAR_RADIO_GROUP
            ? null
            : eventSearchForm.locationVerification;
    const selfInitiated =
        eventSearchForm.selfInitiated === DUMMY_VALUE_TO_CLEAR_RADIO_GROUP
            ? null
            : eventSearchForm.selfInitiated;
    const locations =
        eventSearchForm.locations?.map((location) => ({
            ...location,
            subPremiseDtos:
                location.subPremiseDtos?.filter(
                    (subPremiseDto) => !isEqual(subPremiseDto, defaultSubPremiseDto)
                ) ?? [],
        })) ?? [];
    const persons =
        eventSearchForm.persons?.filter((person) => !isEqual(person, personFormDefaultValues)) ??
        [];
    const vehicles =
        eventSearchForm.vehicles
            ?.filter((vehicle) => !isEqual(vehicle, vehicleFormDefaultValues))
            .map(prepareVehicleForSubmit) ?? [];
    return {
        ...eventSearchForm,
        eventNumbers,
        reportingEventNumbers,
        locationVerification,
        selfInitiated,
        locations,
        persons,
        vehicles,
    } as EventSearchForm;
}

export function generateEnhancedSearchReport({
    eventIds,
    open,
    bulkEventSearchExportMutation,
    eventSearchForm,
}: {
    eventIds: number[];
    open: DebouncedFunc<(modal: ModalType<ModalName>) => void>;
    bulkEventSearchExportMutation: UseMutationResult<
        CadSearchResource.ExportEventSearchToPdf,
        unknown,
        EventSearchPdfExportRequest,
        unknown
    >;
    eventSearchForm?: EventSearchForm;
}) {
    open({
        type: 'GENERATE_LOG_MODAL',
        props: {
            onSelect: (templateId) => {
                if (!templateId) {
                    return;
                }
                bulkEventSearchExportMutation.mutate({
                    pdfTemplateId: templateId,
                    eventSearchForm,
                    eventIds,
                });
            },
        },
    });
}

function omitIgnoredFields(
    formValues: DeepPartialSkipArrayKey<EventSearchFormType>
): DeepPartialSkipArrayKey<EventSearchFormType> {
    return {
        ...formValues,
        includeActiveEvents: undefined,
        fuzzyMatchingEnabled: undefined,
    };
}

export function isFormModified(
    formValues: DeepPartialSkipArrayKey<EventSearchFormType>,
    defaultFormValues: EventSearchFormType
) {
    return !isEqual(
        prepareForSubmit(omitIgnoredFields(formValues)),
        prepareForSubmit(omitIgnoredFields(defaultFormValues))
    );
}
